import React, { useEffect, useState } from "react";
import { Mail, Send, MessageSquare } from "lucide-react";
import { Helmet } from "react-helmet";
import "aos/dist/aos.css";
import { useFormik } from "formik";
import * as Yup from "yup";

import toast, { Toaster } from 'react-hot-toast';
import { useSEO } from "../Pages/Seo";

const ContactUs = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    import("aos").then((AOS) => {
      AOS.default.init({ duration: 500, once: true });
    });
    window.scrollTo(0, 0);
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string().min(10, "Message must be at least 10 characters").required("Message is required"),
  });

  useSEO({
    title: "Contact Us | Game Zone Hub",
    description: "Get in touch with the Game Zone Hub team. Have questions about our free online games or want to report a bug? We are here to help.",
    keywords: "contact game zone hub, customer support, online games support, free games help",
    canonical: "https://www.gamezonehub.in/contact",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Game Zone Hub",
    "url": "https://www.gamezonehub.in/",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "support@gamezonehub.in",
      "availableLanguage": "English"
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      subject: "General Inquiry",
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setIsSubmitting(true);

      setTimeout(() => {
        toast.success(`Message Sent Successfully! We will contact you soon.`, {
            duration: 4000,
            position: 'top-center',
            style: {
              background: '#1e293b', 
              color: '#fff',
              border: '1px solid #3b82f6',
              padding: '16px',
            },
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#FFFAEE',
            },
        });
        
        setIsSubmitting(false);
        resetForm();
      }, 1000);
    },
  });

  return (
    <div className="min-h-screen overflow-hidden bg-[#0f172a] sm:pt-[120px] pt-[100px] sm:pb-20 pb-11 px-4 text-white">
      
      <Toaster />

      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        <div className="text-center sm:mb-16 mb-11" data-aos="fade-down">
          <h1 className="text-3xl sm:text-5xl font-extrabold mb-4">
            Get in <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Touch</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Have a question about our <strong>free online games</strong>, feedback, or want to report a bug? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          
          <div className="space-y-8" data-aos="fade-right">
            <div className="bg-[#1e293b] sm:p-8 p-6 rounded-3xl border border-white/5 shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/10 p-3 rounded-xl">
                    <Mail className="text-blue-400 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Email Us</h4>
                    <p className="text-gray-400 text-sm mb-1">For general inquiries and support:</p>
                    <a href="mailto:support@gamezonehub.in" className="text-blue-400 hover:text-blue-300 font-medium transition">
                      support@gamezonehub.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-purple-500/10 p-3 rounded-xl">
                    <MessageSquare className="text-purple-400 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Feedback</h4>
                    <p className="text-gray-400 text-sm">
                      We value your feedback! Let us know how we can improve your gaming experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1e293b] p-8 rounded-3xl border border-white/5 shadow-lg">
               <h3 className="text-xl font-bold mb-4">Frequently Asked Questions (FAQ)</h3>
               <div className="space-y-4 text-sm text-gray-400">
                   <div>
                     <h4 className="text-white font-semibold mb-1">Are all the games really free to play?</h4>
                     <p>Yes, absolutely! Game Zone Hub is dedicated to providing thousands of high-quality online games completely free of charge. You will never need a credit card or subscription to enjoy our library.</p>
                   </div>
                   <div>
                     <h4 className="text-white font-semibold mb-1">Do I need to download or install anything?</h4>
                     <p>No downloads or installations are required! All our games use HTML5 technology, meaning they run directly inside your web browser (like Chrome, Safari, or Edge) instantly, whether you're on a PC or a mobile device.</p>
                   </div>
                   <div>
                     <h4 className="text-white font-semibold mb-1">Why is a game not loading on my device?</h4>
                     <p>If you experience issues, please try clearing your browser cache, disabling ad-blockers, or switching to a modern browser like Google Chrome or Mozilla Firefox. Some games might also take a few seconds to load depending on your internet connection speed.</p>
                   </div>
                   <div>
                     <h4 className="text-white font-semibold mb-1">Can I submit my own game to Game Zone Hub?</h4>
                     <p>We are always looking for exciting new titles from talented developers! Please use the contact form or email us directly with the subject "Game Submission" to get in touch with our publishing team.</p>
                   </div>
                   <div>
                     <h4 className="text-white font-semibold mb-1">Are these games safe for children?</h4>
                     <p>We rigorously review our content to ensure a safe, virus-free, and family-friendly environment. However, since the games are sourced from various developers, we recommend parental supervision for younger children.</p>
                   </div>
               </div>
            </div>
          </div>

          <div className="bg-[#1e293b] p-8 sm:p-10 rounded-3xl border border-white/5 shadow-lg" data-aos="fade-left">
            <h3 className="text-2xl font-bold mb-6">Send us a Message</h3>
            
            <form className="space-y-6" onSubmit={formik.handleSubmit}>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">First Name</label>
                  <input 
                    type="text" 
                    name="firstName"
                    placeholder="John" 
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                      ${formik.touched.firstName && formik.errors.firstName ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                    `}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.firstName}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Last Name</label>
                  <input 
                    type="text" 
                    name="lastName"
                    placeholder="Doe" 
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                      ${formik.touched.lastName && formik.errors.lastName ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                    `}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.lastName}</div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  placeholder="john@example.com" 
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                    ${formik.touched.email && formik.errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                  `}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Subject</label>
                <select 
                  name="subject"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.subject}
                  className="w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Report a Bug">Report a Bug</option>
                    <option value="Advertising">Advertising</option>
                    <option value="Game Request">Game Request</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Message</label>
                <textarea 
                  rows="4" 
                  name="message"
                  placeholder="How can we help you?" 
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.message}
                  className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                    ${formik.touched.message && formik.errors.message ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                  `}
                ></textarea>
                {formik.touched.message && formik.errors.message && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.message}</div>
                )}
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/20 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <> <Send size={20} /> Send Message </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>


    </div>
  );
};

export default ContactUs;